import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../../components/ui/sheet';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { RotateCcw, AlertTriangle, RefreshCw, Trash2, X, Link as LinkIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import { useAuth } from '../../../contexts/AuthContext';
import { Skeleton } from '../../../components/ui/skeleton';
import { usePermissions } from '../../../hooks/usePermissions';

export default function AllDeviceAssignments() {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('assigned');
  const [currentPage, setCurrentPage] = useState(1);
  const [inPoolPage, setInPoolPage] = useState(1);
  const [deletedPage, setDeletedPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [inPoolTotalPages, setInPoolTotalPages] = useState(1);
  const [deletedTotalPages, setDeletedTotalPages] = useState(1);
  const [restoreDialog, setRestoreDialog] = useState({ isOpen: false, assignment: null });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, assignment: null });
  const [assignDialog, setAssignDialog] = useState({ isOpen: false, assignment: null });
  const [unassignSheet, setUnassignSheet] = useState({ isOpen: false, assignment: null });
  const [unassignNotes, setUnassignNotes] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const { get, post, delete: deleteApi } = useApi();
  const { can, PERMISSIONS } = usePermissions();

  useEffect(() => {
    fetchAssignments();
  }, [currentPage, inPoolPage, deletedPage, activeTab]);

  const handleRefresh = () => {
    fetchAssignments();
  };

  // Get account ID from user object
  const getAccountId = () => {
    return user?.admin_id || user?.id || user?._id || user?.admin_id || user?.userId || user?.user_id || null;
  };
  const getAssignmentId = (a) => a?._id || a?.id || a?.assignment_id || a?.uuid || null;

  const fetchAssignments = async () => {
    try {
      setIsLoading(true);
      
      
      // Build query parameters
      let page = currentPage;
      if (activeTab === 'in-pool') {
        page = inPoolPage;
      } else if (activeTab === 'deleted') {
        page = deletedPage;
      }
      
      const params = new URLSearchParams({
        per_page: itemsPerPage,
        page: page
      });

      // Add admin_id if available
      const accountId = getAccountId();
      if (accountId) {
        params.append('admin_id', accountId);
      }

      // Add filter based on tab
      if (activeTab === 'deleted') {
        params.append('deleted', 'true');
      } else if (activeTab === 'in-pool') {
        params.append('in_pool', 'true');
      } else if (activeTab === 'assigned') {
        params.append('active', 'true');
      }

      const response = await get(`/device-assignments?${params.toString()}`);
      
      
      if (response?.data && Array.isArray(response?.data)) {
        // API already filters by tab, so we can use the data directly
        // But we'll also apply client-side filtering as a safety measure
        setAssignments(response.data);
        if (response.meta) {
          if (activeTab === 'assigned') {
            setTotalPages(response.meta.lastPage || response.meta.last_page || 1);
          } else if (activeTab === 'in-pool') {
            setInPoolTotalPages(response.meta.lastPage || response.meta.last_page || 1);
          } else {
            setDeletedTotalPages(response.meta.lastPage || response.meta.last_page || 1);
          }
        }
      } else {
        setAssignments([]);
      }
      
    } catch (error) {
      
      let errorMessage = 'An error occurred while fetching device assignments. Please try again.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please login again.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.status === 404) {
        errorMessage = 'API endpoint not found. Please check the server configuration.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setAssignments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteDialog = (assignment) => {
    const assignmentId = getAssignmentId(assignment);
    setDeleteDialog({ isOpen: true, assignment, id: assignmentId });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ isOpen: false, assignment: null, id: null });
  };

  const openAssignDialog = async (assignment) => {
    setAssignDialog({ isOpen: true, assignment });
    setAssignNotes('');
  };

  const closeAssignDialog = () => {
    setAssignDialog({ isOpen: false, assignment: null });
    setAssignNotes('');
  };

  const handleDeleteAssignment = async () => {
    if (!deleteDialog.assignment) return;

    const assignmentId = deleteDialog.id ?? getAssignmentId(deleteDialog.assignment);
    if (!assignmentId) {
      toast.error('Assignment ID missing');
      return;
    }
    
    try {
      
      const response = await deleteApi(`/device-assignments/${assignmentId}`);
      
      
      // Update the local state to mark as deleted (add unassignedAt timestamp)
      setAssignments(prevAssignments => 
        prevAssignments.map(assignment => {
          const id = assignment._id || assignment.id;
          return id === assignmentId 
            ? { ...assignment, unassignedAt: new Date().toISOString() }
            : assignment;
        })
      );
      
      toast.success('Device assignment deleted successfully');
      closeDeleteDialog();
      // Refresh data after successful deletion
      await fetchAssignments();
    } catch (error) {
      
      let errorMessage = 'An error occurred while deleting the device assignment. Please try again.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const openRestoreDialog = (assignment) => {
    const assignmentId = getAssignmentId(assignment);
    setRestoreDialog({ isOpen: true, assignment,id: assignmentId });
  };

  const closeRestoreDialog = () => {
    setRestoreDialog({ isOpen: false, assignment: null,id: null });
  };

  const handleAssignDevice = async () => {
    if (!assignDialog.assignment) return;

    const assignment = assignDialog.assignment;
    const deviceId = assignment.deviceId || assignment.device_id || assignment.device?._id || assignment.device?.id;
    const accountId = assignment.accountId || assignment.account_id || assignment.account?._id || assignment.account?.id;
    
    if (!deviceId) {
      toast.error('Device ID missing');
      return;
    }

    if (!accountId) {
      toast.error('Account ID missing');
      return;
    }

    try {
      const userId = user?.id || user?._id || user?.userId || user?.user_id || null;
      
      const payload = {
        device_id: deviceId,
        account_id: accountId,
        notes: assignNotes || '',
        assigned_by: userId
      };


      const response = await post('/device-assignments', payload);


      toast.success(response.message || 'Device assigned successfully');
      closeAssignDialog();
      // Switch to assigned tab and refresh data after successful assignment
      setActiveTab('assigned');
      setCurrentPage(1);
      await fetchAssignments();
    } catch (error) {

      let errorMessage = 'An error occurred while assigning the device. Please try again.';

      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    }
  };

  const handleRestoreAssignment = async () => {
    if (!restoreDialog.assignment) return;
    const assignmentId = restoreDialog.id ?? getAssignmentId(restoreDialog.assignment);
    if (!assignmentId) {
      toast.error('Assignment ID missing');
      return;
    }
    
    try {
      // Check multiple possible locations for device ID
      /*const deviceId = restoreDialog.assignment.device_id || 
                       restoreDialog.assignment.device?.id || 
                       restoreDialog.assignment.device?._id;*/
      
      /*
      if (!deviceId) {
        toast.error('Device ID not found in assignment data');
        return;
      }*/
      
      const response = await post(`/device-assignments/${assignmentId}/restore`);
      
      
      // Update the local state to mark as active (remove unassignedAt and change status back to Assigned)
      setAssignments(prevAssignments => 
        prevAssignments.map(assignment => {
          const id = assignment._id || assignment.id;
          return id === assignmentId
            ? { 
                ...assignment, 
                unassignedAt: null,
                device: {
                  ...assignment.device,
                  deviceStatus: 'Assigned',
                  device_status: 'Assigned'
                }
              }
            : assignment;
        })
      );
      
      toast.success(response.message || 'Assignment restored successfully');
      closeRestoreDialog();
      // Switch to assigned tab and refresh data after successful restore
      setActiveTab('assigned');
      setCurrentPage(1);
      await fetchAssignments();
    } catch (error) {
      
      let errorMessage = 'An error occurred while restoring the device assignment. Please try again.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const openUnassignSheet = (assignment) => {
    setUnassignSheet({ isOpen: true, assignment });
    setUnassignNotes('');
  };

  const closeUnassignSheet = () => {
    setUnassignSheet({ isOpen: false, assignment: null });
    setUnassignNotes('');
  };

  const handleUnassignDevice = async () => {
    const a = unassignSheet.assignment;
    if (!a) return;

    const assignmentId = getAssignmentId(a);
    if (!assignmentId) {
      toast.error('Assignment ID missing');
      return;
    }
    if (!unassignSheet.assignment) return;
    
    try {
      
      const payload = {
        unassigned_by: user?.id || user?. userId || user?.user_id || null,
        notes: unassignNotes || ''
      };
      
      
      const response = await post(`/device-assignments/${assignmentId}/unassign`, payload);
      
      
      // Update the local state to mark as unassigned (add unassignedAt timestamp and change status)
      setAssignments(prevAssignments => 
        prevAssignments.map(assignment => {
          const id = assignment._id || assignment.id;
          return id === assignmentId 
            ? { 
                ...assignment, 
                unassignedAt: new Date().toISOString(),
                device: {
                  ...assignment.device,
                  deviceStatus: 'Unassigned',
                  device_status: 'Unassigned'
                }
              }
            : assignment;
        })
      );
      
      toast.success(response.message || 'Device unassigned successfully');
      closeUnassignSheet();
      // Switch to in-pool tab and refresh data after successful unassign
      setActiveTab('in-pool');
      setInPoolPage(1);
      await fetchAssignments();
    } catch (error) {
      
      let errorMessage = 'An error occurred while unassigning the device. Please try again.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handlePageChange = (page) => {
    if (activeTab === 'assigned') {
      setCurrentPage(page);
    } else if (activeTab === 'in-pool') {
      setInPoolPage(page);
    } else {
      setDeletedPage(page);
    }
  };

  // Filter assignments based on tab
  // Note: API already filters by tab, but we do client-side filtering as a safety measure
  const getFilteredAssignments = () => {
    // Since API already filters by tab, return all assignments
    // The API should handle the filtering, but we'll do minimal client-side validation
    return assignments;
    
    // Uncomment below if API doesn't filter properly and you need client-side filtering:
    /*
    if (activeTab === 'deleted') {
      // Show only deleted assignments (those with unassignedAt or deletedAt)
      return assignments.filter(a => {
        const unassigned = a.unassignedAt || a.unassigned_at;
        const deleted = a.deletedAt || a.deleted_at;
        return unassigned || deleted;
      });
    } else if (activeTab === 'in-pool') {
      // Show only in-pool assignments (devices that are in pool/unassigned)
      return assignments.filter(a => {
        const unassigned = a.unassignedAt || a.unassigned_at;
        const deleted = a.deletedAt || a.deleted_at;
        const deviceStatus = a.device?.deviceStatus || a.device?.device_status;
        // In pool means: unassigned but not deleted, OR status is In Pool/Available
        return (unassigned && !deleted) || 
               deviceStatus === 'In Pool' || 
               deviceStatus === 'in_pool' || 
               deviceStatus === 'Available' ||
               deviceStatus === 'available';
      });
    } else {
      // Show only assigned assignments (those without unassignedAt and status is Assigned)
      return assignments.filter(a => {
        const unassigned = a.unassignedAt || a.unassigned_at;
        const deleted = a.deletedAt || a.deleted_at;
        const deviceStatus = a.device?.deviceStatus || a.device?.device_status;
        // Assigned means: no unassignedAt, no deletedAt, and status is Assigned
        return !unassigned && !deleted && 
               (deviceStatus === 'Assigned' || deviceStatus === 'assigned');
      });
    }
    */
  };

  const filteredAssignments = getFilteredAssignments();

  // Skeleton loading component
  const SkeletonRow = () => (
    <TableRow className="h-8">
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-4 w-20" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-4 w-32" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2 hidden sm:table-cell">
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-4 w-36" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2 hidden md:table-cell">
        <Skeleton className="h-4 w-32" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-6 w-16 rounded-full" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <div className="flex items-center gap-0.5 sm:gap-1">
          <Skeleton className="h-6 w-6 sm:h-8 sm:w-8 rounded" />
          <Skeleton className="h-6 w-6 sm:h-8 sm:w-8 rounded" />
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="p-2 sm:p-3 lg:p-4 max-w-full overflow-hidden bg-background text-foreground min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
        <h1 className="text-xl sm:text-2xl font-bold">Device Assignments</h1>
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            className="flex items-center gap-2 flex-shrink-0 border-0 bg-muted/50"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => {
        setActiveTab(value);
        // Reset to page 1 when switching tabs
        if (value === 'assigned') {
          setCurrentPage(1);
        } else if (value === 'in-pool') {
          setInPoolPage(1);
        } else {
          setDeletedPage(1);
        }
      }} className="w-full">
        <TabsList className="grid w-full max-w-lg grid-cols-3 mb-4">
          <TabsTrigger value="assigned">Assigned</TabsTrigger>
          <TabsTrigger value="in-pool">In Pool</TabsTrigger>
          <TabsTrigger value="deleted">Deleted</TabsTrigger>
        </TabsList>

        <TabsContent value="assigned" className="mt-0">
          {/* Assigned Assignments Table */}
          <div className="bg-card text-card-foreground rounded-sm overflow-x-auto">
            <Table className="text-xs sm:text-sm min-w-full">
              <TableHeader>
                <TableRow className="h-8 bg-muted/50">
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[120px]">Date</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[120px] sm:min-w-[150px]">Device Name</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[130px] hidden sm:table-cell">IMEI Number</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[120px] sm:min-w-[150px]">Account</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[140px] hidden md:table-cell">Email</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[100px]">Status</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  // Show skeleton rows while loading
                  Array.from({ length: 5 }).map((_, index) => (
                    <SkeletonRow key={`skeleton-${index}`} />
                  ))
                ) : filteredAssignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No assigned assignments found
                    </TableCell>
                  </TableRow>
                ) : (
                  // Show actual data when loaded
                  filteredAssignments.map((assignment) => (
                <TableRow key={assignment._id || assignment.id} className="h-8">
                  <TableCell className="px-2 sm:px-3 py-2 cursor-pointer hover:text-primary">
                    <span className="text-xs sm:text-sm">
                      {new Date(assignment.assignedAt || assignment.assigned_at).toLocaleDateString('en-GB')}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2">
                    <span className="text-xs sm:text-sm truncate max-w-[100px] sm:max-w-[130px]">
                      {assignment.device?.deviceName || assignment.device?.device_name || 'N/A'}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2 hidden sm:table-cell">
                    <span className="text-xs sm:text-sm  truncate max-w-[100px] sm:max-w-[120px]">
                      {assignment.device?.imeiNumber || assignment.device?.imei_number || 'N/A'}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2">
                    <span className="text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[140px]">
                      {assignment.account?.name || 'N/A'}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2 hidden md:table-cell">
                    <span className="text-xs sm:text-sm truncate max-w-[100px] sm:max-w-[130px]">
                      {assignment.account?.email || 'N/A'}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2">
                    <span className={`px-1 py-0.5 rounded text-xs sm:text-sm font-medium ${
                      assignment.device?.deviceStatus === 'Assigned' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' 
                        : assignment.device?.deviceStatus=== 'Unassigned'
                        ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100'
                    }`}>
                      {assignment.device?.deviceStatus || 'Unknown'}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2">
                    <div className="flex items-center gap-0.5 sm:gap-1">
                      {can(PERMISSIONS.EDIT_DEVICE_ASSIGNMENT) && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openUnassignSheet(assignment)}
                          className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-orange-600 hover:text-orange-700"
                          title="Unassign Device"
                        >
                          <X className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      )}
                      {can(PERMISSIONS.DELETE_DEVICE_ASSIGNMENT) && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openDeleteDialog(assignment)}
                          className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-red-600 hover:text-red-700"
                          title="Delete Assignment"
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Assigned Pagination */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-4">
            {isLoading ? (
              <>
                <Skeleton className="h-5 w-48" />
                <div className="flex items-center gap-2 flex-wrap">
                  <Skeleton className="h-8 w-16" />
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-12" />
                </div>
              </>
            ) : (
              <>
                <div className="text-sm text-muted-foreground">
                  Showing page {currentPage} of {totalPages} | Total: {filteredAssignments.length} assignments
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                    disabled={currentPage === 1}
                    className="hover:bg-muted/50"
                  >
                    Previous
                  </Button>
                  {/* Show page 1 if not in range */}
                  {currentPage > 2 && totalPages > 3 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        className={`${
                          currentPage === 1 
                            ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                            : 'text-foreground hover:bg-muted/50'
                        }`}
                      >
                        1
                      </Button>
                      {currentPage > 3 && <span className="px-2 text-muted-foreground">...</span>}
                    </>
                  )}
                  {/* Show pages around current page */}
                  {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                    let page;
                    if (totalPages <= 5) {
                      page = i + 1;
                    } else if (currentPage <= 3) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + i;
                    } else {
                      page = currentPage - 2 + i;
                    }
                    return page;
                  })
                  .filter((page, index, arr) => arr.indexOf(page) === index) // Remove duplicates
                  .filter(page => page >= 1 && page <= totalPages) // Ensure valid range
                  .map(page => (
                    <Button
                      key={page}
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page)}
                      className={`${
                        currentPage === page 
                          ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                          : 'text-foreground hover:bg-muted/50'
                      }`}
                    >
                      {page}
                    </Button>
                  ))}
                  {/* Show last page if not in range */}
                  {currentPage < totalPages - 2 && totalPages > 3 && (
                    <>
                      {currentPage < totalPages - 3 && <span className="px-2 text-muted-foreground">...</span>}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(totalPages)}
                        className={`${
                          currentPage === totalPages 
                            ? 'bg-secondary/50 text-secondary-foreground font-semibold hover:bg-secondary/90' 
                            : 'text-foreground hover:bg-muted/50'
                        }`}
                      >
                        {totalPages}
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="hover:bg-muted/50"
                  >
                    Next
                  </Button>
                </div>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="in-pool" className="mt-0">
          {/* In Pool Assignments Table */}
          <div className="bg-card text-card-foreground rounded-sm overflow-x-auto">
            <Table className="text-xs sm:text-sm min-w-full">
              <TableHeader>
                <TableRow className="h-8 bg-muted/50">
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[120px]">Date</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[120px] sm:min-w-[150px]">Device Name</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[130px] hidden sm:table-cell">IMEI Number</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[120px] sm:min-w-[150px]">Account</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[140px] hidden md:table-cell">Email</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[100px]">Status</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  // Show skeleton rows while loading
                  Array.from({ length: 5 }).map((_, index) => (
                    <SkeletonRow key={`skeleton-inpool-${index}`} />
                  ))
                ) : filteredAssignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No in-pool assignments found
                    </TableCell>
                  </TableRow>
                ) : (
                  // Show actual data when loaded
                  filteredAssignments.map((assignment) => (
                    <TableRow key={assignment._id || assignment.id} className="h-8">
                      <TableCell className="px-2 sm:px-3 py-2 cursor-pointer hover:text-primary">
                        <span className="text-xs sm:text-sm">
                          {new Date(assignment.assignedAt || assignment.assigned_at).toLocaleDateString('en-GB')}
                        </span>
                      </TableCell>
                      <TableCell className="px-2 sm:px-3 py-2">
                        <span className="text-xs sm:text-sm truncate max-w-[100px] sm:max-w-[130px]">
                          {assignment.device?.deviceName || assignment.device?.device_name || 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell className="px-2 sm:px-3 py-2 hidden sm:table-cell">
                        <span className="text-xs sm:text-sm truncate max-w-[100px] sm:max-w-[120px]">
                          {assignment.device?.imeiNumber || assignment.device?.imei_number || 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell className="px-2 sm:px-3 py-2">
                        <span className="text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[140px]">
                          {assignment.account?.name || 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell className="px-2 sm:px-3 py-2 hidden md:table-cell">
                        <span className="text-xs sm:text-sm truncate max-w-[100px] sm:max-w-[130px]">
                          {assignment.account?.email || 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell className="px-2 sm:px-3 py-2">
                        <span className={`px-1 py-0.5 rounded text-xs sm:text-sm font-medium ${
                          (assignment.device?.deviceStatus === 'In Pool' || assignment.device?.deviceStatus === 'in_pool' || assignment.device?.deviceStatus === 'Available' || assignment.device?.deviceStatus === 'available')
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100' 
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100'
                        }`}>
                          {assignment.device?.deviceStatus || 'Unknown'}
                        </span>
                      </TableCell>
                      <TableCell className="px-2 sm:px-3 py-2">
                        <div className="flex items-center gap-0.5 sm:gap-1">
                          {can(PERMISSIONS.EDIT_DEVICE_ASSIGNMENT) && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openAssignDialog(assignment)}
                              className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-green-600 hover:text-green-700"
                              title="Assign Device"
                            >
                              <LinkIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          )}
                          {can(PERMISSIONS.EDIT_DEVICE_ASSIGNMENT) && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openUnassignSheet(assignment)}
                              className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-orange-600 hover:text-orange-700"
                              title="Unassign Device"
                            >
                              <X className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          )}
                          {can(PERMISSIONS.DELETE_DEVICE_ASSIGNMENT) && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openDeleteDialog(assignment)}
                              className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-red-600 hover:text-red-700"
                              title="Delete Assignment"
                            >
                              <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* In Pool Pagination */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-4">
            {isLoading ? (
              <>
                <Skeleton className="h-5 w-48" />
                <div className="flex items-center gap-2 flex-wrap">
                  <Skeleton className="h-8 w-16" />
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-12" />
                </div>
              </>
            ) : (
              <>
                <div className="text-sm text-muted-foreground">
                  Showing page {inPoolPage} of {inPoolTotalPages} | Total: {filteredAssignments.length} in-pool assignments
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(Math.max(inPoolPage - 1, 1))}
                    disabled={inPoolPage === 1}
                    className="hover:bg-muted/50"
                  >
                    Previous
                  </Button>
                  {/* Show page 1 if not in range */}
                  {inPoolPage > 2 && inPoolTotalPages > 3 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        className={`${
                          inPoolPage === 1 
                            ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                            : 'text-foreground hover:bg-muted/50'
                        }`}
                      >
                        1
                      </Button>
                      {inPoolPage > 3 && <span className="px-2 text-muted-foreground">...</span>}
                    </>
                  )}
                  {/* Show pages around current page */}
                  {Array.from({ length: Math.min(inPoolTotalPages, 3) }, (_, i) => {
                    let page;
                    if (inPoolTotalPages <= 5) {
                      page = i + 1;
                    } else if (inPoolPage <= 3) {
                      page = i + 1;
                    } else if (inPoolPage >= inPoolTotalPages - 2) {
                      page = inPoolTotalPages - 4 + i;
                    } else {
                      page = inPoolPage - 2 + i;
                    }
                    return page;
                  })
                  .filter((page, index, arr) => arr.indexOf(page) === index) // Remove duplicates
                  .filter(page => page >= 1 && page <= inPoolTotalPages) // Ensure valid range
                  .map(page => (
                    <Button
                      key={page}
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page)}
                      className={`${
                        inPoolPage === page 
                          ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                          : 'text-foreground hover:bg-muted/50'
                      }`}
                    >
                      {page}
                    </Button>
                  ))}
                  {/* Show last page if not in range */}
                  {inPoolPage < inPoolTotalPages - 2 && inPoolTotalPages > 3 && (
                    <>
                      {inPoolPage < inPoolTotalPages - 3 && <span className="px-2 text-muted-foreground">...</span>}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(inPoolTotalPages)}
                        className={`${
                          inPoolPage === inPoolTotalPages 
                            ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                            : 'text-foreground hover:bg-muted/50'
                        }`}
                      >
                        {inPoolTotalPages}
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(Math.min(inPoolPage + 1, inPoolTotalPages))}
                    disabled={inPoolPage === inPoolTotalPages}
                    className="hover:bg-muted/50"
                  >
                    Next
                  </Button>
                </div>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="deleted" className="mt-0">
          {/* Deleted Assignments Table */}
          <div className="bg-card text-card-foreground rounded-sm overflow-x-auto">
            <Table className="text-xs sm:text-sm min-w-full border-1 border-muted-foreground/20">
              <TableHeader>
                <TableRow className="h-8 bg-muted/50">
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[120px]">Date</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[120px] sm:min-w-[150px]">Device Name</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[130px] hidden sm:table-cell">IMEI Number</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[120px] sm:min-w-[150px]">Account</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[140px] hidden md:table-cell">Email</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[140px]">Deleted At</TableHead>
                  <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  // Show skeleton rows while loading
                  Array.from({ length: 5 }).map((_, index) => (
                    <SkeletonRow key={`skeleton-deleted-${index}`} />
                  ))
                ) : filteredAssignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No deleted assignments found
                    </TableCell>
                  </TableRow>
                ) : (
                  // Show actual data when loaded
                  filteredAssignments.map((assignment) => (
                    <TableRow key={assignment._id || assignment.id} className="h-8">
                      <TableCell className="px-2 sm:px-3 py-2 cursor-pointer hover:text-primary">
                        <span className="text-xs sm:text-sm">
                          {new Date(assignment.assignedAt || assignment.assigned_at).toLocaleDateString('en-GB')}
                        </span>
                      </TableCell>
                      <TableCell className="px-2 sm:px-3 py-2">
                        <span className="text-xs sm:text-sm truncate max-w-[100px] sm:max-w-[130px]">
                          {assignment.device?.deviceName || assignment.device?.device_name || 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell className="px-2 sm:px-3 py-2 hidden sm:table-cell">
                        <span className="text-xs sm:text-sm truncate max-w-[100px] sm:max-w-[120px]">
                          {assignment.device?.imeiNumber || assignment.device?.imei_number || 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell className="px-2 sm:px-3 py-2">
                        <span className="text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[140px]">
                          {assignment.account?.name || 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell className="px-2 sm:px-3 py-2 hidden md:table-cell">
                        <span className="text-xs sm:text-sm truncate max-w-[100px] sm:max-w-[130px]">
                          {assignment.account?.email || 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell className="px-2 sm:px-3 py-2">
                        <span className="text-xs sm:text-sm">
                          {assignment.unassignedAt || assignment.unassigned_at || assignment.deletedAt || assignment.deleted_at
                            ? new Date(assignment.unassignedAt || assignment.unassigned_at || assignment.deletedAt || assignment.deleted_at).toLocaleDateString('en-GB')
                            : 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell className="px-2 sm:px-3 py-2">
                        <div className="flex items-center gap-0.5 sm:gap-1">
                          {can(PERMISSIONS.EDIT_DEVICE_ASSIGNMENT) && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openRestoreDialog(assignment)}
                              className="h-6 w-6 sm:h-8 sm:w-8 p-0 border-0 bg-muted/50"
                              title="Restore Assignment"
                            >
                              <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Deleted Pagination */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-4">
            {isLoading ? (
              <>
                <Skeleton className="h-5 w-48" />
                <div className="flex items-center gap-2 flex-wrap">
                  <Skeleton className="h-8 w-16" />
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-12" />
                </div>
              </>
            ) : (
              <>
                <div className="text-sm text-muted-foreground">
                  Showing page {deletedPage} of {deletedTotalPages} | Total: {filteredAssignments.length} deleted assignments
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(Math.max(deletedPage - 1, 1))}
                    disabled={deletedPage === 1}
                    className="hover:bg-muted/50"
                  >
                    Previous
                  </Button>
                  {/* Show page 1 if not in range */}
                  {deletedPage > 2 && deletedTotalPages > 3 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        className={`${
                          deletedPage === 1 
                            ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                            : 'text-foreground hover:bg-muted/50'
                        }`}
                      >
                        1
                      </Button>
                      {deletedPage > 3 && <span className="px-2 text-muted-foreground">...</span>}
                    </>
                  )}
                  {/* Show pages around current page */}
                  {Array.from({ length: Math.min(deletedTotalPages, 3) }, (_, i) => {
                    let page;
                    if (deletedTotalPages <= 5) {
                      page = i + 1;
                    } else if (deletedPage <= 3) {
                      page = i + 1;
                    } else if (deletedPage >= deletedTotalPages - 2) {
                      page = deletedTotalPages - 4 + i;
                    } else {
                      page = deletedPage - 2 + i;
                    }
                    return page;
                  })
                  .filter((page, index, arr) => arr.indexOf(page) === index) // Remove duplicates
                  .filter(page => page >= 1 && page <= deletedTotalPages) // Ensure valid range
                  .map(page => (
                    <Button
                      key={page}
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page)}
                      className={`${
                        deletedPage === page 
                          ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                          : 'text-foreground hover:bg-muted/50'
                      }`}
                    >
                      {page}
                    </Button>
                  ))}
                  {/* Show last page if not in range */}
                  {deletedPage < deletedTotalPages - 2 && deletedTotalPages > 3 && (
                    <>
                      {deletedPage < deletedTotalPages - 3 && <span className="px-2 text-muted-foreground">...</span>}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(deletedTotalPages)}
                        className={`${
                          deletedPage === deletedTotalPages 
                            ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                            : 'text-foreground hover:bg-muted/50'
                        }`}
                      >
                        {deletedTotalPages}
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(Math.min(deletedPage + 1, deletedTotalPages))}
                    disabled={deletedPage === deletedTotalPages}
                    className="hover:bg-muted/50"
                  >
                    Next
                  </Button>
                </div>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      {deleteDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card text-card-foreground rounded-lg p-6 max-w-md w-full mx-4 shadow-xl border-1 border-muted-foreground/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Delete Device Assignment
                </h3>
                <p className="text-sm text-muted-foreground">
                  This action can be restored later
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-foreground">
                Are you sure you want to delete the assignment for device{' '}
                <span className="font-semibold text-foreground">
                  "{deleteDialog.assignment?.device?.device_name}"
                </span>?
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                This will unassign the device from{' '}
                <span className="font-semibold">
                  {deleteDialog.assignment?.account?.name}
                </span>.
              </p>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={closeDeleteDialog}
                className="px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAssignment}
                className="px-4 py-2 bg-red-600 hover:bg-red-700"
              >
                Delete Assignment
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Dialog */}
      {restoreDialog.isOpen && ( 
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card text-card-foreground rounded-lg p-6 max-w-md w-full mx-4 shadow-xl border-1 border-muted-foreground/20">
            <div className="flex items-center gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Restore Device Assignment
                </h3>
                <p className="text-sm text-muted-foreground">
                  Confirm restore action
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-foreground text-sm">
                Are you sure you want to restore the assignment for device{' '}
                <span className="font-semibold text-foreground">
                  "{restoreDialog.assignment?.device?.device_name}"
                </span>?
              </p>
              <p className="text-foreground text-sm mt-2">
                This will restore the device assignment to{' '}
                <span className="font-semibold">
                  {restoreDialog.assignment?.account?.name}
                </span>.
              </p>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={closeRestoreDialog}
                className="px-4 py-2 border-0 bg-muted/50"
              > 
                Cancel
              </Button>
              <Button
                onClick={handleRestoreAssignment}
                className="px-4 py-2 border-0 bg-green-600 hover:bg-green-700 text-white"
              >
                Restore Assignment
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Device Dialog */}
      {assignDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card text-card-foreground rounded-lg p-6 max-w-md w-full mx-4 shadow-xl border-1 border-muted-foreground/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <LinkIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Assign Device
                </h3>
                <p className="text-sm text-muted-foreground">
                  Assign device to an account
                </p>
              </div>
            </div>
            
            <div className="mb-6 space-y-4">
              <div>
                <p className="text-foreground text-sm mb-2">
                  Device: <span className="font-semibold text-foreground">
                    {assignDialog.assignment?.device?.deviceName || assignDialog.assignment?.device?.device_name || 'N/A'}
                  </span>
                </p>
              </div>

              <div>
                <p className="text-foreground text-sm mb-2">
                  Account: <span className="font-semibold text-foreground">
                    {assignDialog.assignment?.account?.name || 'N/A'}
                  </span>
                </p>
              </div>

              <div>
                <Label htmlFor="assign_notes" className="text-sm font-medium text-foreground mb-2">
                  Notes (Optional)
                </Label>
                <Textarea
                  id="assign_notes"
                  value={assignNotes}
                  onChange={(e) => setAssignNotes(e.target.value)}
                  placeholder="Enter assignment notes..."
                  rows={3}
                  className="w-full mt-2"
                />
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={closeAssignDialog}
                className="px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignDevice}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white"
              >
                Assign Device
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Unassign Device Sheet */}
      <Sheet open={unassignSheet.isOpen} onOpenChange={(open) => !open && closeUnassignSheet()}>
        <SheetContent className="w-[400px] sm:w-[540px] p-0 flex flex-col">
          <SheetHeader className="p-6 pb-4">
            <SheetTitle className="flex items-center gap-2 text-xl">
              <div className="flex-shrink-0 w-8 h-8 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                <X className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              </div>
              Unassign Device
            </SheetTitle>
            <SheetDescription>
              Unassign device from account
            </SheetDescription>
          </SheetHeader>
          
          <div className="flex-1 overflow-y-auto px-6">
            <div className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">Device</p>
                  <p className="text-sm text-muted-foreground">
                    {unassignSheet.assignment?.device?.device_name}
                  </p>
                </div>
                <div className="space-y-2 mt-3">
                  <p className="text-sm font-medium text-foreground">Account</p>
                  <p className="text-sm text-muted-foreground">
                    {unassignSheet.assignment?.account?.name}
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="unassign_notes" className="text-sm font-medium text-foreground mb-2">
                  Notes (Optional)
                </Label>
                <Textarea
                  id="unassign_notes"
                  value={unassignNotes}
                  onChange={(e) => setUnassignNotes(e.target.value)}
                  placeholder="Enter reason for unassignment..."
                  rows={4}
                  className="w-full mt-2"
                />
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 justify-end p-6 pt-4 border-t border-border/20 bg-background">
            <Button
              variant="outline"
              onClick={closeUnassignSheet}
              className="px-4 py-2"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleUnassignDevice}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white"
            >
              Unassign Device
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}